import React, { useEffect, useId, useRef, useState } from 'react';
import { CheckCircle2, ShieldAlert, X } from 'lucide-react';
import { ApiError } from '../services/api.error';
import { submitIllegalContentNotice } from '../services/illegal-content.api';
import { useTranslations } from '../translations';
import PrivacyNotice from './PrivacyNotice';
import './IllegalContentNoticeModal.css';

interface IllegalContentNoticeModalProps {
  onNavigateToPrivacy?: () => void;
  onClose: () => void;
  initialUrl?: string;
}

const IllegalContentNoticeModal: React.FC<IllegalContentNoticeModalProps> = ({ onClose, initialUrl, onNavigateToPrivacy }) => {
  const t = useTranslations();
  const titleId = useId();
  const formId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [identityOmitted, setIdentityOmitted] = useState(false);
  const [contentUrl, setContentUrl] = useState(initialUrl || window.location.href);
  const [explanation, setExplanation] = useState('');
  const [goodFaithConfirmed, setGoodFaithConfirmed] = useState(false);
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [referenceCode, setReferenceCode] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!identityOmitted && (!reporterName.trim() || !reporterEmail.trim())) {
      setError(t.illegalContentNotice.identityRequired);
      return;
    }
    if (explanation.trim().length < 20) {
      setError(t.illegalContentNotice.explanationTooShort);
      return;
    }
    if (!goodFaithConfirmed) {
      setError(t.illegalContentNotice.goodFaithRequired);
      return;
    }

    setSubmitting(true);
    try {
      const receipt = await submitIllegalContentNotice({
        reporterName: identityOmitted ? '' : reporterName.trim(),
        reporterEmail: identityOmitted ? '' : reporterEmail.trim(),
        identityOmitted,
        contentUrl: contentUrl.trim(),
        explanation: explanation.trim(),
        goodFaithConfirmed,
        website,
      });
      setReferenceCode(receipt.referenceCode);
    } catch (requestError) {
      console.error('Error submitting illegal content notice:', requestError);
      setError(requestError instanceof ApiError && requestError.status === 429
        ? t.illegalContentNotice.rateLimited
        : t.illegalContentNotice.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="illegal-notice-overlay" onPointerDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="illegal-notice-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <button ref={closeRef} type="button" className="illegal-notice-close" onClick={onClose} aria-label={t.common.close}>
          <X size={21} aria-hidden="true" />
        </button>

        {referenceCode ? (
          <div className="illegal-notice-success">
            <CheckCircle2 size={42} aria-hidden="true" />
            <h2 id={titleId}>{t.illegalContentNotice.successTitle}</h2>
            <p>{t.illegalContentNotice.successDescription}</p>
            <strong>{referenceCode}</strong>
            <button type="button" className="illegal-notice-primary" onClick={onClose}>{t.common.close}</button>
          </div>
        ) : (
          <>
            <header className="illegal-notice-header">
              <span className="illegal-notice-icon" aria-hidden="true"><ShieldAlert size={24} /></span>
              <div>
                <h2 id={titleId}>{t.illegalContentNotice.title}</h2>
              </div>
            </header>

            <form id={formId} className="illegal-notice-form" onSubmit={handleSubmit} noValidate>
              <p className="illegal-notice-description">{t.illegalContentNotice.description}</p>
              <label>
                <span>{t.illegalContentNotice.contentUrl} *</span>
                <input type="url" required maxLength={2048} value={contentUrl} onChange={(event) => setContentUrl(event.target.value)} />
              </label>

              <label>
                <span>{t.illegalContentNotice.explanation} *</span>
                <textarea required minLength={20} maxLength={5000} rows={5} value={explanation} onChange={(event) => setExplanation(event.target.value)} placeholder={t.illegalContentNotice.explanationPlaceholder} />
                <small>{explanation.length}/5000</small>
              </label>

              <label className="illegal-notice-checkbox">
                <input type="checkbox" checked={identityOmitted} onChange={(event) => setIdentityOmitted(event.target.checked)} />
                <span>{t.illegalContentNotice.identityException}</span>
              </label>

              {!identityOmitted && (
                <div className="illegal-notice-identity">
                  <label>
                    <span>{t.illegalContentNotice.name} *</span>
                    <input type="text" required maxLength={100} value={reporterName} onChange={(event) => setReporterName(event.target.value)} />
                  </label>
                  <label>
                    <span>{t.illegalContentNotice.email} *</span>
                    <input type="email" required maxLength={254} value={reporterEmail} onChange={(event) => setReporterEmail(event.target.value)} />
                  </label>
                </div>
              )}

              <label className="illegal-notice-honeypot" aria-hidden="true">
                Website
                <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
              </label>

              <label className="illegal-notice-checkbox illegal-notice-good-faith">
                <input type="checkbox" required checked={goodFaithConfirmed} onChange={(event) => setGoodFaithConfirmed(event.target.checked)} />
                <span>{t.illegalContentNotice.goodFaith}</span>
              </label>

              <PrivacyNotice onNavigateToPrivacy={onNavigateToPrivacy} />

              {error && <p className="illegal-notice-error" role="alert">{error}</p>}
            </form>
            <div className="illegal-notice-actions">
              <button type="button" className="illegal-notice-secondary" onClick={onClose}>{t.common.cancel}</button>
              <button type="submit" form={formId} className="illegal-notice-primary" disabled={submitting}>
                {submitting ? t.illegalContentNotice.submitting : t.illegalContentNotice.submit}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default IllegalContentNoticeModal;
